import { Button, Checkbox, Space } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomModal from "../../../../components/ui/CustomModal";
import { dateFormat } from "../../../../constants/constants";
import { usePettyCashSendMutation } from "../../../../features/pettyCash/pettyCash";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import { isExpiredWithoutToday } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import { ModalComponentProps } from "../../../../types";
import { formatSouthAsianWithDecimals } from "../../../../utils/utils";
import WarningIcon from "../_icons/WarningIcon";
import { SingleSelectedDataType } from "./PettyCashDetails";

export default function SendSingleDisbursement({
  handleClose,
  data,
  ...rest
}: ModalComponentProps<SingleSelectedDataType>) {
  const [agree, setAgree] = useState<boolean>(false);
  const [pettyCashSend, { isLoading, isError, error, isSuccess }] =
    usePettyCashSendMutation();
  useError(isError, error, "Send disbursement fail");
  const {
    handleOpen,
    open,
    handleClose: downloadClose,
  } = useModal<undefined, "DOWNLOAD">();

  const handleDownload = () => {
    const data = open?.data;

    // Format into readable text
    const content = (data as unknown as any[])
      ?.map(
        (item) =>
          `Name: ${item.employeeName}\nEmail: ${
            item.employeeEmail
          }\nMissing Fields:\n- ${item.missingFields.join("\n- ")}\n`
      )
      .join("\n----------------------\n\n");

    // Create and download file
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "error-log.txt";
    link.click();
    URL.revokeObjectURL(url);
    handleClose(true);
  };

  useEffect(() => {
    if (
      isError &&
      (error as { data: { data: any } })?.data?.data?.employeesWithMissingInfo
        ?.length
    ) {
      handleOpen({
        type: "DOWNLOAD",
        data: (error as { data: { data: any } })?.data?.data
          ?.employeesWithMissingInfo,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, error]);

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Disbursement sent successfully");
      handleClose(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <CustomModal
      title=""
      footer={null}
      width={450}
      closeIcon={null}
      handleClose={handleClose}
      {...rest}
    >
      {open.type === "DOWNLOAD" && (
        <ConfirmModal
          open={open?.open}
          handleClose={downloadClose}
          handleOk={handleDownload}
          confirmTitle="Error Detected"
          questionText={(error as { data: { message: string } })?.data?.message}
          okText="Download"
        />
      )}
      <WarningIcon />
      {/* <h6 className="text-zinc-700 text-lg font-semibold mt-2.5">
        {data?.userInfo?.due_date &&
        isExpiredWithoutToday(data?.userInfo?.due_date)
          ? "Due date already over."
          : ""}{" "}
        Are you sure?
        <br />
        Send 1 request for disbursement?
      </h6> */}
      <h6 className="text-zinc-700 text-lg font-semibold mt-2.5">
        {data?.userInfo?.due_date &&
        isExpiredWithoutToday(data?.userInfo?.due_date) ? (
          <span>
            Due date has already passed. Are you sure you want to send 1 request
            for disbursement?
          </span>
        ) : (
          "Are you sure you want to send 1 request for disbursement?"
        )}
      </h6>

      <div className="px-2.5 py-1.5 rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-200 space-y-2 mt-4">
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-1 text-slate-400 text-base font-medium">
            Employee:
          </div>
          <div className="col-span-2 text-slate-600 text-base font-medium">
            {data?.userInfo?.name}
          </div>
        </div>
        <div className="outline outline-1 outline-offset-[-0.50px] outline-slate-100" />
        <div className="grid grid-cols-3 gap-2 pt-2">
          <div className="col-span-1 text-slate-400 text-base font-medium">
            Amount:
          </div>
          <div className="text-violet-500 text-base font-semibold">
            {" "}
            ৳{formatSouthAsianWithDecimals(Number(data?.amount))}
          </div>
        </div>
        <div className="outline outline-1 outline-offset-[-0.50px] outline-slate-100" />
        <div className="grid grid-cols-3 gap-2 pt-2">
          <div className="col-span-1 text-slate-400 text-base font-medium">
            Due:
          </div>
          <div className="col-span-2 text-slate-600 text-base font-medium">
            {data?.userInfo?.due_date
              ? moment.utc(data?.userInfo?.due_date).format(dateFormat)
              : "-"}
          </div>
        </div>
      </div>

      <div className="my-4">
        <Checkbox
          className="!select-none !font-medium"
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
        >
          Notify employee by email
        </Checkbox>
      </div>

      <Space size={12}>
        <Button
          className="normal-dark-button modal-button"
          onClick={handleClose}
          size="middle"
          block
        >
          Cancel
        </Button>
        <Button
          className="modal-button"
          type="primary"
          htmlType="submit"
          size="middle"
          block
          loading={isLoading}
          onClick={() =>
            pettyCashSend({
              body: {
                petty_cash_ids: [Number(data?.id)],
                notify_by_email: agree,
              },
            })
          }
        >
          Send
        </Button>
      </Space>
    </CustomModal>
  );
}
